import {
  type AreaRowCalc,
  type AreaRowInput,
  calcAreaRow,
  calcDamageProvision,
  calcFacilityRow,
  calcSummaryTotals,
  type DamageCalc,
  type DamageInput,
  type FacilityRowCalc,
  type FacilityRowInput,
  type FinancialConfig,
  type SummaryTotals,
} from "./index";

function round2<T extends object>(obj: T): T {
  const out = {} as T;
  for (const key in obj) {
    const value = obj[key];
    out[key] = (typeof value === "number" ? Math.round(value * 100) / 100 : value) as T[typeof key];
  }
  return out;
}

export interface RecalcRecordInput {
  config: FinancialConfig;
  certYear: number;
  areas: AreaRowInput[];
  facilities: FacilityRowInput[];
  damage: DamageInput | null;
  rounding: { reinstatement: number; inflation: number; demolition: number; drc: number };
  numberOfUnits: number;
}

export interface RecalcRecordResult {
  areas: AreaRowCalc[];
  facilities: FacilityRowCalc[];
  damage: DamageCalc | null;
  summary: SummaryTotals;
}

// Rows are rounded to the cent first, and the summary is built from those
// rounded rows, so on-screen row totals always foot to the displayed summary.
export function recalcRecord(input: RecalcRecordInput): RecalcRecordResult {
  const areas = input.areas.map((a) => round2(calcAreaRow(a, input.config, input.certYear)));
  const facilities = input.facilities.map((f) => round2(calcFacilityRow(f, input.config)));
  const damage = input.damage ? round2(calcDamageProvision(input.damage, input.config)) : null;
  const summary = round2(calcSummaryTotals(areas, facilities, damage, input.rounding, input.numberOfUnits));

  return { areas, facilities, damage, summary };
}

export function resolveCertYear(
  certificateDate: string | Date | null | undefined,
  pocFrom: string | Date | null | undefined,
): number {
  const cd = certificateDate ? new Date(certificateDate) : null;
  if (cd && !Number.isNaN(cd.getTime())) return cd.getFullYear();

  const pf = pocFrom ? new Date(pocFrom) : null;
  if (pf && !Number.isNaN(pf.getTime())) return pf.getFullYear();

  return new Date().getFullYear();
}
